// "Columnas borradas que todavía tienen datos", dentro de la ventana de
// Columnas de Renovaciones, Prospecto y Referido y Comisiones.
//
// Ticket de Marilyn Sequera, 2026-10-28 (E-391): al añadir columnas se borraban
// las demás. Los valores siguen guardados con el id de la columna; los nombres
// no. Aquí se ve cada una con unos ejemplos para reconocerla, se le pone nombre
// y "Recuperar" la devuelve a la lista de la ventana. Al pulsar Guardar, vuelve
// a la tabla con sus datos.
import { useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { getColumnasHuerfanas } from '../api.js';
import { IS_DEMO } from '../demoMode.js';

export function ColumnasParaRecuperar({ modulo, yaEnLaLista, onRecuperar, lang }) {
  const es = lang === 'es';
  const [huerfanas, setHuerfanas] = useState([]);
  const [nombres, setNombres] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    // El demo no tiene servidor: no hay nada que recuperar ni aviso que dar.
    if (IS_DEMO) return undefined;
    let vivo = true;
    getColumnasHuerfanas(modulo)
      .then((d) => { if (vivo) setHuerfanas(Array.isArray(d) ? d : []); })
      .catch((e) => { if (vivo) setError(e?.message || 'error'); });
    return () => { vivo = false; };
  }, [modulo]);

  const visibles = huerfanas.filter((h) => !yaEnLaLista.has(h.id));
  if (error) {
    return <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)' }}>
      {es ? 'No se pudieron buscar columnas borradas para recuperar.' : 'Could not look for deleted columns to recover.'}
    </div>;
  }
  if (!visibles.length) return null;

  return (
    <div style={{ border: '1px solid rgba(255,183,0,.35)', background: 'rgba(255,183,0,.06)', borderRadius: 10, padding: '.7rem .8rem' }}>
      <div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--c-amber,#ffb700)', marginBottom: '.25rem' }}>
        {es ? 'Columnas borradas que todavía tienen datos' : 'Deleted columns that still have data'}
      </div>
      <div style={{ fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)', marginBottom: '.55rem' }}>
        {es
          ? 'Ponle nombre a la que quieras recuperar, pulsa Recuperar y luego Guardar: vuelve a la tabla con sus datos.'
          : 'Name the one you want back, click Recover and then Save: it returns to the table with its data.'}
      </div>
      {visibles.map((h) => (
        <div key={h.id} style={{ display: 'flex', flexWrap: 'wrap', gap: '.45rem', alignItems: 'center', marginBottom: '.45rem' }}>
          <div style={{ flex: '1 1 200px', minWidth: 0, fontSize: '.76rem', color: 'var(--c-text,#c8dff0)' }}>
            <b>{h.filas}</b> {es ? (h.filas === 1 ? 'fila' : 'filas') : (h.filas === 1 ? 'row' : 'rows')}
            {h.ejemplos?.length ? <span style={{ color: 'var(--c-text3,#45688a)' }}> · {es ? 'ej.' : 'e.g.'} {h.ejemplos.map((x) => `"${x}"`).join(', ')}</span> : null}
          </div>
          <input value={nombres[h.id] || ''} maxLength={60}
            onChange={(e) => setNombres((n) => ({ ...n, [h.id]: e.target.value }))}
            placeholder={es ? 'Nombre de la columna' : 'Column name'}
            style={{ flex: '1 1 150px', minWidth: 0, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.4rem .6rem', color: 'var(--c-white,#eef6ff)', fontSize: '.8rem' }} />
          <button type="button" disabled={!String(nombres[h.id] || '').trim()}
            onClick={() => onRecuperar({ id: h.id, name: String(nombres[h.id]).trim(), type: 'text' })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', border: '1px solid var(--c-amber,#ffb700)', borderRadius: 8, padding: '.4rem .7rem', color: 'var(--c-amber,#ffb700)', fontSize: '.78rem', fontWeight: 700, cursor: 'pointer', opacity: String(nombres[h.id] || '').trim() ? 1 : 0.5 }}>
            <RotateCcw size={13} /> {es ? 'Recuperar' : 'Recover'}
          </button>
        </div>
      ))}
    </div>
  );
}
